// DESCRIPTION: Mobile agenda list for the month view — shows events grouped by date,
// rendered below the mini-calendar on screens < 768px.

import React, { useRef, useEffect } from 'react';
import EventCell from './EventCell';
import { formatTime } from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

/**
 * Format a date string as a human-readable heading.
 *
 * @param {string} dateStr YYYY-MM-DD
 *
 * @return {string}
 */
function formatDateHeading( dateStr ) {
	const d = new Date( dateStr + 'T00:00:00' );
	return d.toLocaleDateString( undefined, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
	} );
}

/**
 * MobileAgenda
 *
 * @param {Object}   props
 * @param {Array}    props.events       All events for the visible month range.
 * @param {string}   props.dateFrom     First visible date (ISO).
 * @param {string}   props.dateTo       Last visible date (ISO).
 * @param {string}   props.selectedDate The date tapped in the mini-calendar (ISO).
 * @param {Function} props.onDayClick   Called with a Date when the user taps a day heading.
 */
export default function MobileAgenda( {
	events,
	dateFrom,
	dateTo,
	selectedDate,
	onDayClick,
} ) {
	const containerRef = useRef( null );
	const dateRefs = useRef( {} );

	// Group events by date.
	const groups = {};
	events.forEach( ( event ) => {
		const key = event.date_utc || event.date || '';
		if ( key < dateFrom || key > dateTo ) {
			return;
		}
		if ( ! groups[ key ] ) {
			groups[ key ] = [];
		}
		groups[ key ].push( event );
	} );

	const sortedDates = Object.keys( groups ).sort();

	// Scroll to the selected date when it changes.
	useEffect( () => {
		if ( selectedDate && dateRefs.current[ selectedDate ] ) {
			dateRefs.current[ selectedDate ].scrollIntoView( {
				behavior: 'smooth',
				block: 'start',
			} );
		}
	}, [ selectedDate ] );

	if ( sortedDates.length === 0 ) {
		return (
			<p className="nb-calendar-error">
				{ strings.noEvents || 'No events this period.' }
			</p>
		);
	}

	return (
		<div className="nb-mobile-agenda" ref={ containerRef }>
			{ sortedDates.map( ( dateStr ) => {
				const dayEvents = groups[ dateStr ];
				const isSelected = dateStr === selectedDate;

				return (
					<div
						key={ dateStr }
						ref={ ( el ) => {
							dateRefs.current[ dateStr ] = el;
						} }
						className={ `nb-mobile-agenda__day${
							isSelected ? ' nb-mobile-agenda__day--selected' : ''
						}` }
					>
						<button
							className="nb-mobile-agenda__day-heading"
							onClick={ () =>
								onDayClick( new Date( dateStr + 'T00:00:00' ) )
							}
						>
							{ formatDateHeading( dateStr ) }
						</button>

						{ dayEvents.map( ( event ) => (
							<div
								key={ event.id }
								className="nb-mobile-agenda__event"
							>
								{ event.time &&
									config.showEventTime !== false && (
										<span className="nb-mobile-agenda__event-time">
											{ formatTime( event.time ) }
										</span>
									) }
								<EventCell
									event={ event }
									isAllDay={ ! event.time }
								/>
							</div>
						) ) }
					</div>
				);
			} ) }
		</div>
	);
}
